import { api } from "../../../api/apiSlice";

// Vehicle Master (handoff §20.1) - this session's scope is the master
// roster only, not Maintenance Requests (§20.3+, a separate not-yet-built
// screen). vehicleId is globally unique across every branch (unlike
// custId/empId/productCode's branch-local collisions), so - like Employee -
// no {branchId} composite key is needed in any single-vehicle route.
//
// Verified live against the deployed API before wiring: PUT silently
// defaults `isActive` to false when the field is omitted from the body (it
// is NOT preserved from the existing record) - every update must always
// send the vehicle's current isActive value explicitly, never omit it.
export const vehiclesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getVehicles: builder.query({
      query: (params) => ({ url: "/vehicles", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((v) => ({ type: "Vehicles", id: v.vehicleId })),
              { type: "Vehicles", id: "LIST" },
            ]
          : [{ type: "Vehicles", id: "LIST" }],
    }),
    getVehicle: builder.query({
      query: (vehicleId) => ({ url: `/vehicles/${vehicleId}` }),
      providesTags: (result, error, vehicleId) => [{ type: "Vehicles", id: vehicleId }],
    }),
    createVehicle: builder.mutation({
      query: (body) => ({ url: "/vehicles", method: "POST", data: body }),
      invalidatesTags: [{ type: "Vehicles", id: "LIST" }],
    }),
    updateVehicle: builder.mutation({
      query: ({ vehicleId, ...body }) => ({
        url: `/vehicles/${vehicleId}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (result, error, { vehicleId }) => [
        { type: "Vehicles", id: vehicleId },
        { type: "Vehicles", id: "LIST" },
      ],
    }),
    // Maintenance Requests - read-only here, only what Stock Management's
    // "Pending Vehicle Requests" LPO picker needs (an APPROVED request's
    // Spares Needed line items). Full request create/approve/reject/
    // attachment management is a separate, not-yet-built screen - out of
    // scope for this pair of endpoints.
    listMaintenanceRequests: builder.query({
      query: (params) => ({ url: "/vehicles/requests", params }),
      providesTags: [{ type: "VehicleRequests", id: "LIST" }],
    }),
    getMaintenanceRequest: builder.query({
      query: (recordGuid) => ({ url: `/vehicles/requests/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "VehicleRequests", id: recordGuid }],
    }),
    // Approve / reject a SUBMITTED request (2026-09-10). Both endpoints and the
    // Vehicles.ApproveRequest policy have existed server-side since Batch40 -
    // only the React screen was missing, so a manager had to use the Flutter app
    // to approve. Requested directly: "make that pending requests page show
    // submitted requestes also so the managers can approve from react itself".
    //
    // Approving auto-creates the Petty Cash voucher server-side (VehiclesService
    // calls InsertVoucherAsync), so PettyCashVouchers is invalidated too or the
    // voucher list would look unchanged until a refresh.
    approveMaintenanceRequest: builder.mutation({
      query: ({ recordGuid, cashAmount, approvalComment }) => ({
        url: `/vehicles/requests/${recordGuid}/approve`,
        method: "POST",
        data: { cashAmount, approvalComment },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "VehicleRequests", id: recordGuid },
        { type: "VehicleRequests", id: "LIST" },
        { type: "PettyCashVoucher", id: "LIST" },
      ],
    }),
    // The request's one attachment (2026-09-23): odometer photo from the app, or any
    // photo / PDF attached from the web at any time. Same blob contract as the petty
    // cash receipt.
    uploadMaintenanceRequestAttachment: builder.mutation({
      query: ({ recordGuid, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return { url: `/vehicles/requests/${recordGuid}/attachment`, method: "POST", data: formData };
      },
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "VehicleRequests", id: recordGuid },
        { type: "VehicleRequests", id: "LIST" },
      ],
    }),
    getMaintenanceRequestAttachmentContent: builder.query({
      query: (recordGuid) => ({ url: `/vehicles/requests/${recordGuid}/attachment`, responseType: "blob" }),
    }),
    deleteMaintenanceRequestAttachment: builder.mutation({
      query: (recordGuid) => ({ url: `/vehicles/requests/${recordGuid}/attachment`, method: "DELETE" }),
      invalidatesTags: (result, error, recordGuid) => [
        { type: "VehicleRequests", id: recordGuid },
        { type: "VehicleRequests", id: "LIST" },
      ],
    }),
    rejectMaintenanceRequest: builder.mutation({
      query: ({ recordGuid, rejectionReason }) => ({
        url: `/vehicles/requests/${recordGuid}/reject`,
        method: "POST",
        data: { rejectionReason },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "VehicleRequests", id: recordGuid },
        { type: "VehicleRequests", id: "LIST" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetVehiclesQuery,
  useGetVehicleQuery,
  useCreateVehicleMutation,
  useUpdateVehicleMutation,
  useListMaintenanceRequestsQuery,
  useGetMaintenanceRequestQuery,
  useLazyGetMaintenanceRequestQuery,
  useApproveMaintenanceRequestMutation,
  useRejectMaintenanceRequestMutation,
  useUploadMaintenanceRequestAttachmentMutation,
  useLazyGetMaintenanceRequestAttachmentContentQuery,
  useDeleteMaintenanceRequestAttachmentMutation,
} = vehiclesApi;
